import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import PaperSummary from "@/components/PaperSummary";
import { toast } from "sonner";
import { useConfirmation } from "@/context/ConfirmationContext";
import {
  Star,
  ArrowLeft,
  Download,
  CaretDown,
  BookmarkSimple,
  PencilSimple,
  FloppyDisk,
  Trash,
  Plus,
  X,
  Image as ImageIcon,
  CheckCircle,
  ArrowClockwise,
  WarningCircle,
} from "@phosphor-icons/react";
import { instructionLines } from "@/lib/instructionLines";
import MathText from "@/components/MathText";
import MatchTheFollowing from "@/components/MatchTheFollowing";
import QuestionDiagram from "@/components/QuestionDiagram";
import BrandingLogo from "@/components/BrandingLogo";
import { printDocument, diagramsReady } from "@/lib/print";
import { canRegenerate, regeneratingId } from "@/lib/regenerate";
import {
  canRepairInternalChoices,
  internalChoiceRecovery,
  internalChoiceRepairLive,
} from "@/lib/internalChoiceRepair";
import { DEFAULT_PAPER_INSTRUCTIONS, formatMark } from "@/lib/exportFormat";
import { canonicalQuestionType, questionTypeLabel } from "@/lib/questionType";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const typeBadge = (t) => {
  if (t === "information") return "qp-badge qp-badge-blue";
  if (t === "application") return "qp-badge qp-badge-red";
  return "qp-badge qp-badge-yellow";
};

const TYPE_OPTIONS = ["information", "concept", "application"];
const DIFF_OPTIONS = ["easy", "medium", "hard"];

// A compound question's first scored part has both a paper number and a part
// label. Keep those two pieces together: later parts intentionally render only
// their part label, while ordinary questions have no part label to append.
export const questionDisplayLabel = (questionNumber, partLabel) => {
  if (questionNumber == null) return partLabel || "";
  return `Q${questionNumber}.${partLabel ? ` ${partLabel}` : ""}`;
};

const GENERATION_PHASE_LABELS = {
  queued: "Preparing",
  resolve: "Preparing",
  repairing: "Checking and completing",
  alternatives: "Checking and completing",
  figures: "Checking and completing",
  preparing: "Preparing",
  generating: "Drafting",
  validating: "Checking and completing",
  finalizing: "Checking and completing",
  ready: "Ready",
  failed: "Generation failed",
  cancelled: "Generation cancelled",
};


export default function PaperView() {
  const confirm = useConfirmation();
  const { id } = useParams();
  const [paper, setPaper] = useState(null);
  const qualityReviewRunning = paper?.quality_review?.status === "running";
  const paperWorkActive = ["queued", "pending", "generating", "reviewing"].includes(paper?.generation_status)
    || ["queued", "running"].includes(paper?.completion?.status)
    || ["queued", "running"].includes(paper?.quality_review?.status)
    || paper?.topup?.status === "generating"
    || paper?.regen?.status === "generating";
  const exportWarningIgnored = Boolean(paper?.export_warning_acknowledgement?.ignored) && !paperWorkActive;
  const qualityReviewBlocked = !exportWarningIgnored && (
    qualityReviewRunning
    || paper?.quality_review?.status === "needs_review"
    || (paper?.completion && !paper.completion.export_eligible)
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [topupCount, setTopupCount] = useState(0);
  const [queuing, setQueuing] = useState(false);
    // The question whose regenerate button was just clicked. Covers the gap
  // before the reload brings back paper.regen, which is the real source of
  // truth for the spinner.
  const [regenId, setRegenId] = useState(null);
  const [cancellingGeneration, setCancellingGeneration] = useState(false);
  const [docxDownloading, setDocxDownloading] = useState(false);
  const [ignoringExportWarning, setIgnoringExportWarning] = useState(false);
  const [brandingApplying, setBrandingApplying] = useState(false);
  const [brandingPresets, setBrandingPresets] = useState([]);
  const [selectedBrandingId, setSelectedBrandingId] = useState("");

  const loadSequence = useRef(0);
  const load = async () => {
    const sequence = ++loadSequence.current;
    const { data } = await api.get(`/papers/${id}`);
    if (sequence !== loadSequence.current) return;
    setPaper(current => {
      if (current?.id === data.id && (current.generation_revision || 0) > (data.generation_revision || 0)) return current;
      return data;
    });
  };

  useEffect(() => {
    load();
    api.get("/branding-presets")
      .then(({ data }) => setBrandingPresets(data))
      .catch(() => {});
  }, [id]); // eslint-disable-line

  // Poll for diagram completion when any are still pending.
  useEffect(() => {
    if (!paper || editing) return;
    if ((paper.diagrams_pending || 0) === 0) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [paper, editing]); // eslint-disable-line

  useEffect(() => {
    if (!paper || editing) return;
    if (!["queued", "recovering"].includes(paper.generation_recovery?.status)) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [paper, editing]); // eslint-disable-line

  // Poll while the paper itself is still being generated by the LLM.
  useEffect(() => {
    if (!paper || editing) return;
    if (!["queued", "pending", "generating", "reviewing"].includes(paper.generation_status)) return;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [paper, editing]); // eslint-disable-line

  // A usable owner draft is published before repairs finish. Keep refreshing
  // the visible questions and quality banner while those checks run.
  useEffect(() => {
    if (!paper || editing || paper.quality_review?.status !== "running") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [paper, editing]); // eslint-disable-line

  // Poll while a top-up is filling in the missing questions. Deliberately does
  // not gate on generation_status: the paper stays "ready" throughout so the
  // recovered questions remain on screen while the rest arrive.
  useEffect(() => {
    if (!paper || editing) return;
    if (paper.topup?.status !== "generating") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [paper, editing]); // eslint-disable-line

  // Poll while one question is being rewritten. Faster than the top-up's 4s:
  // this is a single LLM call and the teacher is watching that one line.
  useEffect(() => {
    if (!paper || editing) return;
    if (paper.regen?.status !== "generating") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [paper, editing]); // eslint-disable-line

  // Full restart. Destructive once a partial paper exists, so the caller that
  // can reach it from the partial banner confirms first.
  const regenerateAll = async () => {
    try {
      await api.post(`/papers/${id}/regenerate`);
      toast.success("Retrying — the AI is drafting questions again");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not retry");
    }
  };

  const cancelGeneration = async () => {
    setCancellingGeneration(true);
    try {
      const { data } = await api.post(`/papers/${id}/cancel-generation`);
      ++loadSequence.current;
      setPaper(current => ({ ...current, ...data }));
      await load();
      toast.success("Paper generation cancelled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel generation");
    } finally {
      setCancellingGeneration(false);
    }
  };

  // Fill in only the questions that never arrived. Unlike regenerateAll this
  // cannot destroy anything — the backend appends and never rewrites an
  // existing question — so there is no confirm dialog.
  const generateMissing = async (count) => {
    setQueuing(true);
    try {
      const { data } = await api.post(`/papers/${id}/generate-missing`, {
        count: count || null,
      });
      if (data.status === "queued") {
        toast.success("Completing pending questions and OR alternatives. Your paper remains available.");
      } else if (data.queued === 0) {
        toast.info("Nothing missing — this paper already has every question");
      } else {
        toast.success(
          `Generating ${data.queued} more question${data.queued === 1 ? "" : "s"}...`
        );
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setQueuing(false);
    }
  };

  const ignoreExportWarning = async () => {
    setIgnoringExportWarning(true);
    try {
      const { data } = await api.post(`/papers/${id}/ignore-export-warning`);
      ++loadSequence.current;
      setPaper(data);
      toast.success("Warning ignored — downloads are now available");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not ignore this warning");
    } finally {
      setIgnoringExportWarning(false);
    }
  };

  const retryTemplateRecovery = async () => {
    try {
      await api.post(`/papers/${id}/retry-template-recovery`);
      toast.success("Template recovery started. Your current paper remains available.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not retry template recovery");
    }
  };

  const repairInternalChoices = async () => {
    try {
      await api.post(`/papers/${id}/repair-internal-choices`);
      toast.success("Repairing the missing internal OR choices — unaffected questions stay as they are");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not repair the internal OR choices");
    }
  };

  const enterEdit = () => {
    if (qualityReviewRunning) {
      toast.error("Editing is paused until the final paper checks finish.");
      return;
    }
    setDraft(JSON.parse(JSON.stringify(paper)));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: draft.title,
        ...(draft.instructions !== paper.instructions ? { instructions: draft.instructions } : {}),
        duration_minutes: Number(draft.duration_minutes),
        total_marks: Number(draft.total_marks),
        sections: draft.sections.map((s) => ({
          title: s.title,
          section_id: s.section_id || null,
          questions: s.questions,
        })),
      };
      const { data } = await api.patch(`/papers/${id}`, payload);
      ++loadSequence.current;
      setPaper(data);
      setDraft(null);
      setEditing(false);
      toast.success("Paper saved — AI will learn from your edits");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateQ = (si, qi, patch) => {
    const next = { ...draft };
    next.sections = next.sections.map((s, i) =>
      i === si
        ? {
            ...s,
            questions: s.questions.map((q, j) =>
              j === qi ? { ...q, ...patch } : q
            ),
          }
        : s
    );
    setDraft(next);
  };

  const deleteQ = (si, qi) => {
    const next = { ...draft };
    next.sections = next.sections.map((s, i) =>
      i === si
        ? { ...s, questions: s.questions.filter((_, j) => j !== qi) }
        : s
    );
    setDraft(next);
  };

  const addQ = (si) => {
    const next = { ...draft };
    next.sections = next.sections.map((s, i) =>
      i === si
        ? {
            ...s,
            questions: [
              ...s.questions,
              {
                id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                question: "",
                type: "concept",
                difficulty: "medium",
                marks: 2,
                important: false,
                needs_diagram: false,
              },
            ],
          }
        : s
    );
    setDraft(next);
  };

  const toggleImportant = async (qid) => {
    try {
      const { data } = await api.patch(
        `/papers/${id}/question/${qid}/toggle-important`
      );
      ++loadSequence.current;
      setPaper({ ...paper, sections: data.sections });
    } catch {
      toast.error("Failed");
    }
  };

  const saveToBank = async (qid) => {
    try {
      await api.post(`/qbank/save-from-paper/${id}/${qid}`);
      toast.success("Saved to question bank");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // Replace one question with a different one in the same slot. The backend
  // runs it in the background and reports through paper.regen, so this only
  // starts the job; the polling effect above brings the new question in.
  const regenerateQuestion = async (qid) => {
    setRegenId(qid);
    try {
      await api.post(`/papers/${id}/question/${qid}/regenerate`);
      toast.success("Rewriting this question — your other questions stay as they are");
      // A reload rather than a local patch: the server recomputes `summary` on
      // every GET, so splicing sections here would leave the side panel stale.
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate this question");
    } finally {
      setRegenId(null);
    }
  };

  // Saved via the browser's print engine rather than the backend renderer, so
  // the PDF keeps the fonts and KaTeX math shown on screen.
  const download = () => {
    if (qualityReviewBlocked) {
      toast.error("Complete all required questions before downloading");
      return;
    }
    if (!diagramsReady()) {
      toast.error("Diagrams are still loading — try again in a moment");
      return;
    }
    printDocument(paper.title);
  };

  const downloadDocx = async () => {
    setDocxDownloading(true);
    try {
      const { data } = await api.get(`/papers/${id}/docx`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(paper.title || "paper").replace(/[^a-z0-9 _-]/gi, "").slice(0, 60) || "paper"}.docx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Editable DOCX downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not download DOCX");
    } finally {
      setDocxDownloading(false);
    }
  };

  const applyBranding = async () => {
    if (!selectedBrandingId) {
      toast.error("Choose a branding preset first");
      return;
    }
    setBrandingApplying(true);
    try {
      const { data } = await api.post(`/papers/${id}/branding/apply`, {
        branding_id: selectedBrandingId,
      });
      ++loadSequence.current;
      setPaper(data);
      setSelectedBrandingId("");
      toast.success("Current branding applied to this paper");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply branding");
    } finally {
      setBrandingApplying(false);
    }
  };

  const removeBranding = async () => {
    setBrandingApplying(true);
    try {
      const { data } = await api.delete(`/papers/${id}/branding`);
      ++loadSequence.current;
      setPaper(data);
      toast.success("Branding removed from this paper");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove branding");
    } finally {
      setBrandingApplying(false);
    }
  };

  if (!paper) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-5xl mx-auto p-12 text-neutral-500">
          Loading paper...
        </div>
      </div>
    );
  }

  const hasSavedQuestions = (paper.sections || []).some(section => section.questions?.length);

  if (!hasSavedQuestions && ["queued", "pending", "generating", "reviewing"].includes(paper.generation_status)) {
    const generatedCount = (paper.sections || []).reduce((total, section) => total + (section.questions || []).length, 0) || paper.generation_progress?.completed || 0;
    const configuredCount = paper.generation_progress?.total || paper.target_question_count || paper.generation_requirements?.total_questions || paper.total_questions || 0;
    const generationPhase = paper.generation_progress?.phase || "preparing";
    const phaseLabel = paper.presentation?.stage || GENERATION_PHASE_LABELS[generationPhase] || "Drafting";
    const batch = paper.generation_progress?.batch || 0;
    const batchTotal = paper.generation_progress?.batch_total || 0;
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <main
          className="max-w-3xl mx-auto p-6 md:p-12"
          data-testid="paper-generating-page"
        >
          <Link
            to="/"
            className="qp-btn-ghost inline-flex items-center gap-2 mb-8"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={16} weight="bold" /> Dashboard
          </Link>
          <div
            className="border border-neutral-200 bg-white rounded-2xl p-10 text-center"
            data-testid="paper-generating-card"
          >
            <div className="inline-flex h-12 w-12 rounded-full border-2 border-[#002FA7] border-t-transparent animate-spin mb-6" />
            <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
              {paper.title || "Generating paper…"}
            </h1>
            <p className="text-neutral-600">
              {paper.generation_status === "reviewing" ? "We are validating and completing the remaining questions." : <>Our Engine is drafting questions for <b>{paper.subject}</b> · Class <b>{paper.class_name}</b>.</>}
            </p>
            <p className="text-sm text-neutral-500 mt-4">
              Progress updates automatically through each generation phase.
            </p>
            <div className="generation-progress" aria-live="polite">
              <div className="generation-progress-heading"><span>{phaseLabel}</span><strong>{configuredCount > 0 ? `${generatedCount} / ${configuredCount}` : `${generatedCount} generated`}</strong></div>
              <span>{configuredCount > 0 ? `${generatedCount} of ${configuredCount} scored parts drafted` : "Preparing question count…"}{batchTotal > 1 ? ` · Batch ${batch} of ${batchTotal}` : ""}</span>
            </div>
            <button type="button" onClick={cancelGeneration} disabled={cancellingGeneration} className="qp-btn qp-btn-secondary mt-6" data-testid="cancel-paper-generation">
              <X size={16} weight="bold" /> {cancellingGeneration ? "Cancelling…" : "Cancel generation"}
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (paper.generation_status === "cancelled" && !(paper.sections || []).some(section => section.questions?.length)) {
    return <div className="min-h-screen bg-[#FAFAFA]"><Header /><main className="max-w-3xl mx-auto p-6 md:p-12" data-testid="paper-cancelled-page"><Link to="/" className="qp-btn-ghost inline-flex items-center gap-2 mb-8"><ArrowLeft size={16} weight="bold" /> Dashboard</Link><div className="border border-neutral-200 bg-white rounded-2xl p-10 text-center"><h1 className="text-2xl font-semibold text-neutral-900 mb-2">Generation cancelled</h1><p className="text-neutral-600">Generation has stopped. Saved progress is retained for your next completion attempt.</p><button type="button" onClick={regenerateAll} className="qp-btn qp-btn-primary mt-6"><ArrowClockwise size={16} weight="bold" /> Restart generation</button></div></main></div>;
  }

  if (paper.generation_status === "needs_review" && !hasSavedQuestions) {
    return <div className="min-h-screen bg-[#FAFAFA]"><Header /><main className="max-w-3xl mx-auto p-6 md:p-12"><Link to="/" className="qp-btn-ghost inline-flex items-center gap-2 mb-8"><ArrowLeft size={16} weight="bold" /> Dashboard</Link><div className="border border-amber-200 bg-amber-50 rounded-2xl p-10 text-center"><WarningCircle size={36} className="mx-auto text-amber-700 mb-4" /><h1 className="text-2xl font-semibold text-amber-900 mb-2">Generation could not complete</h1><p className="text-amber-800">{paper.presentation?.message || "Generation needs another completion attempt. Saved progress is retained."}</p><button type="button" onClick={() => generateMissing(null)} className="qp-btn qp-btn-primary mt-6"><ArrowClockwise size={16} weight="bold" /> Finish remaining items</button></div></main></div>;
  }

  if (paper.generation_status === "failed" && !hasSavedQuestions) {
    const savedError =
      paper.generation_error ||
      "The AI could not complete this paper. This is often a temporary LLM provider issue.";
    const errMsg = /could not parse json|incomplete (response|output)|malformed json/i.test(savedError)
      ? "The AI provider returned an incomplete response after automatic recovery. Please retry generation."
      : "Generation stopped before the paper passed its checks. Saved progress can be completed in another attempt.";
    const looksTransient = /502|503|504|gateway|timeout|connection|overloaded/i.test(
      errMsg
    );
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <main
          className="max-w-3xl mx-auto p-6 md:p-12"
          data-testid="paper-failed-page"
        >
          <Link
            to="/"
            className="qp-btn-ghost inline-flex items-center gap-2 mb-8"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={16} weight="bold" /> Dashboard
          </Link>
          <div
            className="border border-red-200 bg-red-50 rounded-2xl p-10"
            data-testid="paper-failed-card"
          >
            <h1 className="text-2xl font-semibold text-red-900 mb-2">
              Generation failed
            </h1>
            <p className="text-red-800 mb-2 text-sm font-mono break-words">
              {errMsg}
            </p>
            {looksTransient && (
              <p className="text-sm text-red-700 mb-5">
                The upstream LLM gateway is having a hiccup (502/timeout). Click
                <b> Retry generation </b>below — the request will reuse your
                blueprint, topics and settings. If retries keep failing for
                several minutes, please wait a bit and try again.
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={regenerateAll}
                className="qp-btn qp-btn-primary"
                data-testid="paper-retry-btn"
              >
                <ArrowClockwise size={16} weight="bold" /> Retry generation
              </button>
              <Link
                to="/papers/new"
                className="qp-btn qp-btn-secondary"
                data-testid="paper-new-from-failed"
              >
                Start a new paper
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Counts are derived from what is actually on the page rather than read
  // from a server counter: papers generated before target_question_count
  // existed have no counter, and the backend falls back to the same formula.
  const allQuestions = (paper.sections || []).flatMap((s) => s.questions || []);
  const have = allQuestions.length;
  const target = paper.target_question_count || 0;
  const missingCount = Math.max(0, target - have);
  const topup = paper.topup;
  const templateRecovery = paper.generation_recovery?.reason === "allocation_metadata"
    && paper.generation_recovery?.status !== "ready"
    ? paper.generation_recovery : null;
  const recoveryLive = ["queued", "recovering"].includes(templateRecovery?.status);
  const choiceRecovery = internalChoiceRecovery(paper);
  const choiceRepairLive = internalChoiceRepairLive(paper);
  const choiceRepairAllowed = canRepairInternalChoices({ paper, editing });
  const completionPending = Boolean(
    (paper.completion && !paper.completion.ready)
    || paper.quality_review?.status === "needs_review"
    || ["needs_review", "failed", "cancelled"].includes(paper.generation_status)
  );
  const completionLive = ["queued", "running"].includes(paper.completion?.status)
    || ["queued", "pending", "generating", "reviewing"].includes(paper.generation_status)
    || qualityReviewRunning;
  const isPartial = !exportWarningIgnored && (paper.recovered_from_truncation || missingCount > 0 || completionPending);
  const busy = queuing || completionLive || topup?.status === "generating";
  // Which question the server is rewriting, and whether the button is offered
  // at all. Both rules live in lib/regenerate so they stay testable and cannot
  // drift from the endpoint's own guards.
  const regenLiveId = regenId || regeneratingId(paper);
  const regenAllowed = canRegenerate({ paper, editing, regenId });
  // What the input shows, what the button says, and what gets sent — one
  // value, so a paper with no reliable target still asks for something.
  const topupValue = topupCount || missingCount || 1;

  const view = editing ? draft : paper;
  const hasAppliedBranding = Boolean(
    paper.branding_id || paper.school_name || paper.header_image_path
    || paper.branding_tagline || paper.branding_footer || paper.branding_watermark
  );
  let counter = 1;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <main className="max-w-7xl mx-auto p-6 md:p-12" data-testid="paper-view-page">
        {completionLive && (
          <div className="no-print mb-6 border-2 border-[#FFC300] bg-yellow-50 p-4" data-testid="paper-quality-running-banner">
            <div className="font-bold uppercase tracking-wider text-sm">Checking and completing</div>
            <p className="text-sm text-neutral-700 mt-1">
              We are completing the remaining questions and alternatives. Downloads unlock when the full paper is ready.
              {paper.completion && ` ${paper.completion.actual_scored_parts || 0}/${paper.completion.target_scored_parts || 0} parts drafted; ${paper.completion.pending_alternatives || 0} alternatives pending. ${paper.completion.actual_printed_questions || 0}/${paper.completion.target_printed_questions || 0} printed questions.`}
              {paper.quality_review?.withheld_count > 0 ? ` ${paper.quality_review.withheld_count} question${paper.quality_review.withheld_count === 1 ? " is" : "s are"} temporarily withheld.` : ""}
            </p>
            <button type="button" onClick={cancelGeneration} className="qp-btn qp-btn-secondary mt-2">Cancel completion</button>
          </div>
        )}
        {!isPartial && !completionLive && !templateRecovery && !choiceRecovery && paper.quality_review?.status === "needs_review" && (
          <div className="no-print mb-6 border-2 border-amber-300 bg-amber-50 p-4" data-testid="paper-quality-review-banner">
            <div className="font-bold uppercase tracking-wider text-sm text-amber-900">Completion needs another attempt</div>
            <p className="text-sm text-amber-800 mt-1">{paper.presentation?.message || "The safe questions remain available while the affected content is reviewed."}</p>
          </div>
        )}
        {!completionLive && templateRecovery && (
          <div className="no-print mb-6 border-2 border-[#FFC300] bg-yellow-50 p-4" data-testid="template-recovery-banner">
            <div className="font-bold uppercase tracking-wider text-sm">
              {recoveryLive ? "Template recovery in progress" : "Template recovery needs review"}
            </div>
            <p className="text-sm text-neutral-700 mt-1">
              {recoveryLive
                ? `Checking a complete replacement against the board template (attempt ${templateRecovery.attempt || 0} of ${templateRecovery.max_attempts || 2}). Your current paper remains available.`
                : "The generated paper could not be proven to match its board-template allocation. Nothing has been replaced."}
            </p>
            {!recoveryLive && <button onClick={retryTemplateRecovery} className="qp-btn qp-btn-primary text-xs mt-3" data-testid="retry-template-recovery-button">Retry template recovery</button>}
          </div>
        )}
        {!completionLive && !templateRecovery && choiceRecovery && (
          <div className="no-print mb-6 border-2 border-[#FFC300] bg-yellow-50 p-4" data-testid="internal-choice-recovery-banner">
            <div className="font-bold uppercase tracking-wider text-sm">
              {choiceRepairLive ? "Repairing internal OR choices" : "Internal OR choices need repair"}
            </div>
            <p className="text-sm text-neutral-700 mt-1">
              {choiceRepairLive
                ? "Rewriting only the affected question blocks with the required student-facing alternatives. Your other questions remain unchanged."
                : "Automatic repair could not restore the required internal OR choices. You can safely retry the targeted repair or edit the paper yourself."}
            </p>
            {!choiceRepairLive && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={repairInternalChoices} disabled={!choiceRepairAllowed} className="qp-btn qp-btn-primary text-xs" data-testid="repair-internal-choices-button">
                  <ArrowClockwise size={14} weight="bold" /> Resolve OR choices
                </button>
                <button onClick={enterEdit} className="qp-btn qp-btn-secondary text-xs" data-testid="edit-internal-choices-button">
                  <PencilSimple size={14} weight="bold" /> Edit manually
                </button>
              </div>
            )}
          </div>
        )}
        {!completionLive && !templateRecovery && !choiceRecovery && topup?.status === "generating" && (
          <div
            className="no-print mb-6 border-2 border-black bg-blue-50 p-4 flex items-center gap-3"
            data-testid="topup-generating-banner"
          >
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin shrink-0" />
            <div className="text-sm">
              <span className="font-bold uppercase tracking-wider">
                Adding {topup.requested} question
                {topup.requested === 1 ? "" : "s"}
              </span>
              <span className="text-neutral-700 font-mono ml-2">
                your existing questions stay exactly as they are.
              </span>
            </div>
          </div>
        )}

        {!isPartial && !completionLive && !templateRecovery && !choiceRecovery && topup?.status === "failed" && (
          <div
            className="no-print mb-6 border-2 border-[#E63946] bg-red-50 p-4"
            data-testid="topup-failed-banner"
          >
            <div className="font-bold uppercase tracking-wider text-sm text-red-900">
              Could not add the missing questions
            </div>
            <p className="text-sm text-red-800 mt-1 break-words">
              {/could not parse json|incomplete (response|output)|malformed json/i.test(topup.error || "")
                ? "The AI provider returned an incomplete response after automatic recovery."
                : "Completion stopped before all items passed their checks. Please retry."}
            </p>
            <p className="text-xs text-red-700 mt-2">
              Nothing was lost — every question below is still here. Try again
              when the provider settles.
            </p>
          </div>
        )}

        {!isPartial && !completionLive && !templateRecovery && !choiceRecovery && !topup && paper.regen?.status === "failed" && (
          <div
            className="no-print mb-6 border-2 border-[#E63946] bg-red-50 p-4"
            data-testid="regen-failed-banner"
          >
            <div className="font-bold uppercase tracking-wider text-sm text-red-900">
              Could not regenerate that question
            </div>
            <p className="text-sm text-red-800 mt-1 font-mono break-words">
              {"The replacement did not pass its checks. The previous question has been retained."}
            </p>
            <p className="text-xs text-red-700 mt-2">
              Nothing was lost — the original question is still on the paper. Try
              again, or edit it by hand.
            </p>
          </div>
        )}

        {isPartial && !completionLive && !templateRecovery && !choiceRecovery && topup?.status !== "generating" && (
          <div
            className="no-print mb-6 border-2 border-[#FFC300] bg-yellow-50 p-4 flex items-start gap-3"
            data-testid="partial-banner"
          >
            <WarningCircle
              size={24}
              weight="fill"
              color="#FFC300"
              className="shrink-0"
            />
            <div className="flex-1">
              <div className="font-bold uppercase tracking-wider text-sm">
                {missingCount > 0
                  ? `${missingCount} question${missingCount === 1 ? "" : "s"} missing`
                  : paper.presentation?.action === "review_placement" ? "Review question placement" : "Questions or alternatives pending"}
              </div>
              <div className="text-sm text-neutral-700 mt-1 font-mono">
                {target > 0
                  ? `${have} of ${target} generated. `
                  : "The AI's response was cut off before this paper was finished. "}
                {paper.presentation?.message || "Complete missing questions and OR alternatives while keeping unaffected questions."}
              </div>
              {!completionPending && !paper.completion && <label className="flex items-center gap-2 mt-3 text-xs font-bold uppercase tracking-wider">
                How many to add
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={topupValue}
                  onChange={(e) => setTopupCount(Number(e.target.value))}
                  className="qp-input w-20 py-1"
                  disabled={busy}
                  data-testid="topup-count-input"
                />
              </label>}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button
                onClick={() => paper.presentation?.action === "review_placement" ? enterEdit() : generateMissing(completionPending || paper.completion ? null : topupValue)}
                disabled={busy}
                className="qp-btn qp-btn-primary text-xs"
                data-testid="generate-missing-questions-button"
              >
                {queuing
                  ? "Starting..."
                  : paper.presentation?.action_label || "Finish remaining items"}
              </button>
              <button
                onClick={async () => {
                  if (await confirm({
                    title: "Regenerate the whole paper?",
                    description: "The questions you already have — and any edits — will be discarded.",
                    confirmLabel: "Regenerate paper",
                    tone: "danger",
                  })) {
                    regenerateAll();
                  }
                }}
                disabled={busy}
                className="qp-btn qp-btn-secondary text-xs"
                data-testid="paper-partial-regenerate-button"
              >
                Regenerate all
              </button>
              <button
                type="button"
                onClick={ignoreExportWarning}
                disabled={busy || ignoringExportWarning}
                className="qp-btn qp-btn-secondary text-xs"
                data-testid="ignore-export-warning-button"
              >
                {ignoringExportWarning ? "Ignoring..." : "Ignore"}
              </button>
            </div>
          </div>
        )}
        {!exportWarningIgnored && !isPartial && paper.quality_review?.status !== "needs_review" && paper.instruction_check?.honoured === false && !busy && !templateRecovery && !choiceRecovery && (
          <div className="no-print mb-6 border border-amber-300 bg-amber-50 p-4"
            data-testid="paper-requirements-check">
            <p className="font-bold text-sm">Some requested settings need review</p>
            <p className="mt-2 text-sm">
              {"Review the requested topics, marks and alternatives before downloading."}
            </p>

          </div>
        )}
        {/* Toolbar */}
        <div className="no-print flex items-center justify-between mb-8 flex-wrap gap-3">
          <Link
            to="/"
            className="qp-btn-ghost inline-flex items-center gap-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={16} weight="bold" /> Dashboard
          </Link>
          <div className="flex flex-wrap gap-2">
            {editing ? (
              <>
                <button
                  onClick={cancelEdit}
                  className="qp-btn qp-btn-secondary"
                  data-testid="cancel-edit-button"
                >
                  <X size={16} weight="bold" /> Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="qp-btn qp-btn-primary"
                  data-testid="save-edit-button"
                >
                  <FloppyDisk size={16} weight="bold" />
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={enterEdit}
                  disabled={qualityReviewRunning}
                  className="qp-btn qp-btn-secondary"
                  data-testid="enter-edit-button"
                >
                  <PencilSimple size={16} weight="bold" /> {qualityReviewRunning ? "Checks running" : "Edit"}
                </button>
                <Link
                  to={`/papers/${id}/solution`}
                  className="qp-btn qp-btn-secondary"
                  data-testid="open-solution-button"
                >
                  <CheckCircle size={16} weight="bold" /> Solution
                </Link>
                <div className="flex items-center gap-1 border-2 border-black px-2" data-testid="paper-branding-status">
                  <span className="text-xs font-bold whitespace-nowrap">{hasAppliedBranding ? `Applied: ${paper.branding_name || "Legacy branding"}` : "No branding applied"}</span>
                  <select value={selectedBrandingId} onChange={(event) => setSelectedBrandingId(event.target.value)} className="text-xs bg-white py-1 max-w-36" aria-label="Branding preset">
                    <option value="">Choose preset</option>
                    {brandingPresets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}
                  </select>
                </div>
                <button onClick={applyBranding} disabled={brandingApplying || !selectedBrandingId} className="qp-btn qp-btn-secondary" data-testid="apply-paper-branding">
                  {brandingApplying ? "Applying..." : hasAppliedBranding ? "Replace Branding" : "Apply Branding"}
                </button>
                {hasAppliedBranding && <button onClick={removeBranding} disabled={brandingApplying} className="qp-btn qp-btn-secondary" data-testid="remove-paper-branding">Remove</button>}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="qp-btn qp-btn-primary" disabled={qualityReviewBlocked} title={qualityReviewBlocked ? "Downloads unlock after final checks pass" : undefined} data-testid="download-menu-button">
                      <Download size={16} weight="bold" /> Download <CaretDown size={14} weight="bold" aria-hidden="true" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50 min-w-36 rounded-none border-2 border-black bg-white p-1 shadow-none">
                    <DropdownMenuItem onSelect={download} className="cursor-pointer rounded-none px-3 py-2 font-bold focus:bg-neutral-100" data-testid="download-pdf-button">
                      <Download size={16} weight="bold" /> PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={downloadDocx} disabled={docxDownloading} className="cursor-pointer rounded-none px-3 py-2 font-bold focus:bg-neutral-100" data-testid="download-docx-button">
                      <Download size={16} weight="bold" /> {docxDownloading ? "Preparing..." : "DOCX"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>

        <div className="paper-with-summary">
        {/* Paper */}
        <div
          className="bg-white border-2 border-black p-8 md:p-12 hard-shadow-static-lg"
          data-testid="paper-preview"
        >
          {view.branding_watermark && <div className="paper-branding-watermark text-center text-xs uppercase tracking-[0.35em] text-neutral-300 mb-2" aria-hidden="true">{view.branding_watermark}</div>}
          <div className="text-center border-b-2 border-black pb-6 mb-6">
            <BrandingLogo paperId={id} hasLogo={Boolean(view.header_image_path)} className="mx-auto mb-3 max-h-24 max-w-32 object-contain" />
            {view.school_name && <div className="font-bold text-lg uppercase tracking-wide mb-2">{view.school_name}</div>}
            {editing ? (
              <input
                value={view.title}
                onChange={(e) =>
                  setDraft({ ...draft, title: e.target.value })
                }
                className="qp-input text-center font-display text-3xl md:text-4xl"
                spellCheck={false}
                data-testid="edit-title-input"
              />
            ) : (
              <h1 className="font-display text-3xl md:text-4xl">
                {view.title}
              </h1>
            )}
            <div className="mt-3 font-mono text-sm text-neutral-700">
              Class <b>{view.class_name}</b> · Subject <b>{view.subject}</b>
            </div>
            {view.branding_tagline && <div className="mt-2 text-sm italic text-neutral-500">{view.branding_tagline}</div>}
            <div className="mt-1 font-mono text-sm text-neutral-700 flex flex-wrap gap-3 justify-center items-center">
              <span>
                Total Marks:{" "}
                {editing ? (
                  <input
                    type="number"
                    value={view.total_marks}
                    onChange={(e) =>
                      setDraft({ ...draft, total_marks: e.target.value })
                    }
                    className="qp-input inline-block w-20 py-1 text-sm"
                    data-testid="edit-marks-input"
                  />
                ) : (
                  <b>{formatMark(view.total_marks)}</b>
                )}
              </span>
              <span>
                Duration:{" "}
                {editing ? (
                  <input
                    type="number"
                    value={view.duration_minutes}
                    onChange={(e) =>
                      setDraft({ ...draft, duration_minutes: e.target.value })
                    }
                    className="qp-input inline-block w-20 py-1 text-sm"
                    data-testid="edit-duration-input"
                  />
                ) : (
                  <b>{view.duration_minutes} min</b>
                )}
              </span>
              <span>
                Difficulty:{" "}
                <b className="uppercase">{view.difficulty}</b>
              </span>
            </div>
          </div>

          {editing ? (
            <textarea
              value={view.instructions || ""}
              onChange={(e) =>
                setDraft({ ...draft, instructions: e.target.value })
              }
              className="qp-input w-full mb-6"
              rows={2}
              placeholder="Instructions (e.g., attempt all questions)"
              spellCheck={false}
              data-testid="edit-instructions-input"
            />
          ) : (
            (
              <section className="paper-instructions mb-6 border border-neutral-300 bg-neutral-50 p-4 text-sm" aria-label="Paper instructions">
                <h2 className="font-bold mb-2">Instructions</h2>
                <div>{instructionLines(view.instructions || DEFAULT_PAPER_INSTRUCTIONS).map((line, index) => <div key={`${line}-${index}`} className="paper-instruction-line"><MathText text={line} /></div>)}</div>
              </section>
            )
          )}

          {(view.sections || []).map((section, si) => {
            let printedParent = null;
            return (
            <section key={si} className="mb-8" data-testid={`section-${si}`}>
              <h2 className="font-display text-xl md:text-2xl text-[#002FA7] mb-4 border-b border-neutral-300 pb-2">
                {section.title}
              </h2>
              {section.section_instructions && (
                <p className="mb-4 italic text-sm" data-testid={`section-instructions-${si}`}>
                  <MathText text={section.section_instructions} />
                </p>
              )}
              <ol className="space-y-4">
                {(section.questions || []).map((q, qi) => {
                  const parentId = q.parent_id;
                  const firstPart = Boolean(parentId && parentId !== printedParent);
                  const passageIndex = firstPart
                    ? (section.questions || []).findIndex((candidate) =>
                        candidate.parent_id === parentId && candidate.shared_stimulus)
                    : -1;
                  const caseStimulus = passageIndex >= 0
                    ? section.questions[passageIndex].shared_stimulus
                    : "";
                  let qNum = null;
                  if (firstPart) {
                    qNum = q.display_number || counter;
                    counter = qNum + 1;
                    printedParent = parentId;
                  } else if (!parentId) {
                    qNum = q.display_number || counter;
                    counter = qNum + 1;
                    printedParent = null;
                  }
                  if (editing) {
                    return (
                      <li
                        key={q.id}
                        className="border-2 border-neutral-300 p-4"
                        data-testid={`edit-question-${q.id}`}
                      >
                        {firstPart && caseStimulus && (
                          <textarea value={caseStimulus}
                            onChange={(e) => updateQ(si, passageIndex, { shared_stimulus: e.target.value })}
                            className="qp-input w-full mb-2" rows={4} spellCheck={false}
                            aria-label={`Case study passage for ${q.id}`} />
                        )}
                        {firstPart && q.parent_lead_in && <p className="text-sm italic mb-2">{q.parent_lead_in}</p>}
                        <div className="flex items-start gap-2 mb-2">
                          <span className="font-bold pt-2">{questionDisplayLabel(qNum, q.part_label)}</span>
                          <textarea
                            value={q.question}
                            onChange={(e) =>
                              updateQ(si, qi, { question: e.target.value })
                            }
                            className="qp-input flex-1"
                            rows={2}
                            spellCheck={false}
                            data-testid={`edit-q-text-${q.id}`}
                          />
                          <button
                            onClick={() => deleteQ(si, qi)}
                            className="qp-btn qp-btn-secondary text-xs shrink-0"
                            data-testid={`edit-q-delete-${q.id}`}
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 sm:pl-6">
                          <select
                            value={q.type}
                            onChange={(e) =>
                              updateQ(si, qi, { type: e.target.value })
                            }
                            className="qp-input text-sm py-1"
                            data-testid={`edit-q-type-${q.id}`}
                          >
                            {TYPE_OPTIONS.map((t) => (
                              <option key={t} value={t}>
                                {questionTypeLabel(t)}
                              </option>
                            ))}
                          </select>
                          <select
                            value={q.difficulty}
                            onChange={(e) =>
                              updateQ(si, qi, { difficulty: e.target.value })
                            }
                            className="qp-input text-sm py-1"
                            data-testid={`edit-q-diff-${q.id}`}
                          >
                            {DIFF_OPTIONS.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            value={q.marks}
                            onChange={(e) =>
                              updateQ(si, qi, {
                                marks: Number(e.target.value),
                              })
                            }
                            className="qp-input text-sm py-1"
                            data-testid={`edit-q-marks-${q.id}`}
                          />
                        </div>
                        {(q.format || "").toLowerCase() === "mcq" &&
                          Array.isArray(q.options) &&
                          q.options.length > 0 && (
                            <div className="mt-3 pl-6 space-y-1.5">
                              <div className="overline text-neutral-500">
                                // MCQ OPTIONS — pick the correct one
                              </div>
                              {q.options.slice(0, 4).map((opt, oi) => {
                                const label = ["(a)", "(b)", "(c)", "(d)"][oi] || `(${oi + 1})`;
                                const isCorrect = Number(q.correct_option) === oi;
                                return (
                                  <div
                                    key={oi}
                                    className="flex items-center gap-2"
                                    data-testid={`edit-q-option-${q.id}-${oi}`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateQ(si, qi, { correct_option: oi })
                                      }
                                      data-testid={`edit-q-correct-${q.id}-${oi}`}
                                      className={`w-7 h-7 border-2 text-xs font-bold shrink-0 ${
                                        isCorrect
                                          ? "border-green-700 bg-green-700 text-white"
                                          : "border-black bg-white"
                                      }`}
                                      title="Mark as correct answer"
                                    >
                                      {label.replace(/[()]/g, "")}
                                    </button>
                                    <input
                                      type="text"
                                      value={opt}
                                      onChange={(e) => {
                                        const next = [...(q.options || [])];
                                        next[oi] = e.target.value;
                                        updateQ(si, qi, { options: next });
                                      }}
                                      className="qp-input text-sm py-1 flex-1"
                                      spellCheck={false}
                                      data-testid={`edit-q-option-input-${q.id}-${oi}`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        {q.completion_pending && <p className="no-print text-sm text-amber-700">{q.completion_pending}</p>}
                        <QuestionDiagram paperId={id} question={q} />
                      </li>
                    );
                  }
                  return (
                    <li
                      key={q.id}
                      className="flex flex-col md:flex-row md:items-start gap-3 border-b border-dashed border-neutral-200 pb-4 last:border-0"
                      data-testid={`question-item-${q.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        {firstPart && caseStimulus && <p className="text-sm mb-2"><MathText text={caseStimulus} /></p>}
                        {firstPart && q.parent_lead_in && <p className="text-sm italic mb-2"><MathText text={q.parent_lead_in} /></p>}
                        <div className="flex items-start gap-2">
                          <span className="font-bold">{questionDisplayLabel(qNum, q.part_label)}</span>
                          {q.important && (
                            <Star
                              size={16}
                              weight="fill"
                              color="#FFC300"
                              className="mt-1"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <MatchTheFollowing text={q.question} />
                          </div>
                          <span className="font-mono font-bold text-sm shrink-0">
                            [{formatMark(q.marks)}]
                          </span>
                        </div>
                        {(q.format || "").toLowerCase() === "mcq" &&
                          Array.isArray(q.options) &&
                          q.options.length > 0 && (
                            <div
                              className="mt-2 pl-6 space-y-1"
                              data-testid={`mcq-options-${q.id}`}
                            >
                              {q.options.slice(0, 4).map((opt, oi) => {
                                const label = ["(a)", "(b)", "(c)", "(d)"][oi] || `(${oi + 1})`;
                                const isCorrect = Number(q.correct_option) === oi;
                                return (
                                  <div
                                    key={oi}
                                    className={`text-sm flex items-start gap-2 ${
                                      isCorrect ? "text-green-800" : ""
                                    }`}
                                    data-testid={`mcq-option-${q.id}-${oi}`}
                                  >
                                    <span className="font-bold w-7 shrink-0">{label}</span>
                                    <span className="flex-1 min-w-0">
                                      <MathText text={String(opt)} />
                                    </span>
                                    {isCorrect && (
                                      <span
                                        className="no-print text-[10px] uppercase tracking-wider font-bold text-green-700 bg-green-50 px-1.5 py-0.5"
                                        data-testid={`mcq-correct-${q.id}`}
                                      >
                                        Correct
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        <div className="no-print mt-2 flex flex-wrap gap-1 pl-6">
                          <span className={typeBadge(canonicalQuestionType(q.type))}>{questionTypeLabel(q.type)}</span>
                          <span className="qp-badge">{q.difficulty}</span>
                          {q.format && (
                            <span
                              className="qp-badge"
                              data-testid={`format-badge-${q.id}`}
                            >
                              {String(q.format).replace(/_/g, " ")}
                            </span>
                          )}
                          {q.diagram_path && (
                            <span className="qp-badge">
                              <ImageIcon size={10} weight="bold" /> diagram
                            </span>
                          )}
                        </div>
                        {q.completion_pending && <p className="no-print text-sm text-amber-700">{q.completion_pending}</p>}
                        <QuestionDiagram paperId={id} question={q} />
                      </div>
                      <div className="no-print flex gap-1 pl-6 md:pl-0">
                        <button
                          onClick={() => toggleImportant(q.id)}
                          className={`qp-btn ${
                            q.important ? "qp-btn-primary" : "qp-btn-secondary"
                          } text-xs`}
                          data-testid={`toggle-important-${q.id}`}
                          title="Mark important"
                        >
                          <Star
                            size={14}
                            weight={q.important ? "fill" : "bold"}
                          />
                        </button>
                        <button
                          onClick={() => saveToBank(q.id)}
                          className="qp-btn qp-btn-secondary text-xs"
                          data-testid={`save-bank-${q.id}`}
                          title="Save to question bank"
                        >
                          <BookmarkSimple size={14} weight="bold" />
                        </button>
                        {/* Read mode only — the edit branch returns above. That
                            is deliberate: `draft` holds unsaved changes that the
                            next save would write over a server-side rewrite. */}
                        <button
                          onClick={() => regenerateQuestion(q.id)}
                          disabled={!regenAllowed}
                          className="qp-btn qp-btn-secondary text-xs"
                          data-testid={`regenerate-question-${q.id}`}
                          title="Replace with a different question in the same slot"
                        >
                          <ArrowClockwise
                            size={14}
                            weight="bold"
                            className={regenLiveId === q.id ? "animate-spin" : ""}
                          />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
              {editing && (
                <button
                  onClick={() => addQ(si)}
                  className="qp-btn qp-btn-secondary text-xs mt-3"
                  data-testid={`add-question-${si}`}
                >
                  <Plus size={14} weight="bold" /> Add question
                </button>
              )}
            </section>
            );
          })}

          <div className="text-center mt-8 pt-4 border-t-2 border-black font-mono text-xs text-neutral-500">
            — End of Paper —
          </div>
          {view.branding_footer && <div className="paper-branding-footer text-center mt-3 font-mono text-xs">{view.branding_footer}</div>}
        </div>
        <PaperSummary paper={paper} editing={editing} />
        </div>
      </main>
    </div>
  );
}
